import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// useDashboardVariant — lets the user switch between the classic dashboard
// and the new briefing dashboard. Persists in localStorage. Default is
// 'classic' until the briefing variant proves itself; users opt in through
// Preferences.
//
// The hook listens for cross-tab changes via the `storage` event so the two
// tabs of the same app stay in sync when the user flips the toggle.
// ---------------------------------------------------------------------------

export type DashboardVariant = 'classic' | 'briefing';

const STORAGE_KEY = 'cadence_dashboard_variant';
const DEFAULT: DashboardVariant = 'classic';

function readStored(): DashboardVariant {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'briefing' || v === 'classic' ? v : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

export function useDashboardVariant() {
  const [variant, setVariantState] = useState<DashboardVariant>(readStored);

  // Cross-tab sync. Cheap: one listener per hook use; the common case is
  // one hook mounted in the <Route> switcher, not many.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setVariantState(readStored());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setVariant = useCallback((next: DashboardVariant) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage can be unavailable in private modes; fall through to state-only.
    }
    setVariantState(next);
  }, []);

  return { variant, setVariant };
}
